import React from 'react'

// Components
import PropTypes from 'prop-types'
import i18n from 'i18next'
import { 
  addDays, 
  differenceInMinutes, 
  isAfter, 
  isWeekend, 
  isSameDay,
  setMinutes,
  setHours,
  getHours,
  getMinutes,
  isFuture
} from 'date-fns'
import {Close, ChevronDown} from '../Icons/index.jsx'
import timeZones from '../Services/timeZones.jsx'
import {
  dateFnsLocalizedFormat,
  dateFnsLocalizedTimeFormat,
  convertTimeToTimezone,
} from '../Services/helpers.jsx'
// import { getInstantQuoteDelivery, getInstantQuoteDeliveryCreation, removeInstantQuoteDelivery } from '../Services/storage.jsx';

const now = new Date()

const getTimeZone = (id) => {
  return timeZones.filter((currentTimeZone) => {
    return currentTimeZone.id === id
  })[0]
}

const findNearTime = (originalTime, toCompareTimes) => {
  return toCompareTimes
    .sort((toCompareTimeA, toCompareTimeB) => {
      return Math.abs(
        differenceInMinutes(originalTime, toCompareTimeA),
      ) < Math.abs(differenceInMinutes(originalTime, toCompareTimeB))
        ? -1
        : 1
    })
    .filter((nearTime) => {
      return isAfter(nearTime, now)
    })[0]
}

const buildDateOptions = () => {
  const nextFortyDays = addDays(now, 40)
  const dates = []
  let currentDate = now
  
  while (currentDate <= nextFortyDays) {
    if (!isWeekend(currentDate)) {
      dates.push(new Date(currentDate))
    }
    currentDate = addDays(currentDate, 1)
  }
  
  return dates
}

const buildTimeOptions = (date) => {
  const timeOptionHours = [9, 11, 13, 15, 17, 19, 21]

  return timeOptionHours.map((hours) => {
    const timeOptionHour = setMinutes(date, 0)
    return setHours(timeOptionHour, hours)
  })
}

const getSelectedDateFromOptions = (date, options) => {
  return options.filter((option) => {
    return isSameDay(date, option)
  })[0]
}

export const autoDeliveryOption = {
  isAuto: true,
}

export default class DeliveryDateModal extends React.Component {
  static defaultProps = {
    isDateAuto: true,
    deliveryDate: autoDeliveryOption,
  }

  static propTypes = {
    onDismiss: PropTypes.func,
    onUpdateDate: PropTypes.func,
    deliveryDate: PropTypes.object,
    deliveryMaxSelectableDate: PropTypes.instanceOf(Date),
    currentTimeZone: PropTypes.object,
  }

  dateOptions = buildDateOptions()

  constructor(props) {
    super(props)
    const selectedDate =
      props.deliveryDate &&
      props.deliveryDate.date &&
      this.isDateAvailable(props.deliveryDate.date)
        ? getSelectedDateFromOptions(props.deliveryDate.date, this.dateOptions)
        : this.dateOptions[0]
    const selectedTime =
      props.deliveryDate &&
      props.deliveryDate.date &&
      this.isDateAvailable(props.deliveryDate.date)
        ? props.deliveryDate.date
        : buildTimeOptions(selectedDate).filter(this.isDateAvailable)[0]
    const selectedTimeZone =
      props.deliveryDate && props.deliveryDate.timeZone
        ? props.deliveryDate.timeZone
        : this.props.currentTimeZone

    this.state = {
      isDateAuto: props.deliveryDate.isAuto,
      selectedDate: selectedDate,
      selectedTime: selectedTime,
      selectedTimeZone: selectedTimeZone,
    }
  }

  preventDismiss = (event) => {
    event.stopPropagation()
  }

  isDateAvailable = (date) => {
    return isAfter(date, now)
  }

  setIsDateAuto = () => {
    this.setState({
      isDateAuto: true,
    })
  }

  setIsNotDateAuto = () => {
    this.setState({
      isDateAuto: false,
    })
  }

  onSelectedDateSelectChange = (event) => {
    const {selectedTimeZone, selectedTime} = this.state
    const selectedDate = new Date(event.target.value)
    const currentSelectedTime =
      selectedTime ||
      buildTimeOptions(selectedDate).filter(this.isDateAvailable)[0]
    const selectedTimeHours = getHours(currentSelectedTime)
    const selectedTimeMinutes = getMinutes(currentSelectedTime)
    const updatedDateWithSelectedTime = new Date(selectedDate)
    updatedDateWithSelectedTime.setHours(selectedTimeHours, selectedTimeMinutes)
    const selectedTimeTimeZoned = convertTimeToTimezone(
      updatedDateWithSelectedTime,
      selectedTimeZone,
    )

    if (this.isDateAvailable(selectedTimeTimeZoned)) {
      this.setState(
        {
          selectedDate: selectedDate,
          selectedDateError: false,
        },
        () => {
          this.setState({
            selectedTime: findNearTime(
              updatedDateWithSelectedTime,
              buildTimeOptions(this.state.selectedDate),
            ),
          })
        },
      )
    } else {
      this.setState({
        selectedDate: selectedDate,
        selectedDateError: true,
      })
    }
  }

  onSelectedTimeSelectChange = (event) => {
    const selectedTime = new Date(event.target.value)
    const timeZone = this.state.selectedTimeZone
    const selectedTimeTimeZoned = convertTimeToTimezone(selectedTime, timeZone)

    if (this.isDateAvailable(selectedTimeTimeZoned)) {
      this.setState({
        selectedTime: selectedTime,
        selectedDateError: false,
      })
    } else {
      this.setState({
        selectedTime: selectedTime,
        selectedDateError: true,
      })
    }
  }

  onSelectedTimeZoneSelectChange = (event) => {
    const timeZoneId = event.target.value
    const timeZone = getTimeZone(timeZoneId)
    const selectedTimeTimeZoned = convertTimeToTimezone(
      this.state.selectedTime,
      timeZone,
    )
    if (this.isDateAvailable(selectedTimeTimeZoned)) {
      this.setState({
        selectedTimeZone: timeZone,
        selectedDateError: false,
      })
    } else {
      this.setState({
        selectedTimeZone: timeZone,
        selectedDateError: true,
      })
    }
  }

  onAddDeliveryDate = () => {
    const {isDateAuto, selectedTime, selectedTimeZone} = this.state
    const {deliveryDate, onUpdateDate, onDismiss} = this.props


    const isAutoChanged = deliveryDate.isAuto !== isDateAuto
    
    // Simplified comparison: always update if not auto and we have a selected time
    const shouldUpdateDate = isAutoChanged || (!isDateAuto && selectedTime)


    if (shouldUpdateDate) {
      if (isDateAuto) {
        onUpdateDate(autoDeliveryOption)
      } else {
        onUpdateDate({
          isAuto: false,
          date: selectedTime,
          timeZone: selectedTimeZone,
        })
      }
    }
    onDismiss()
  }

  render = () => {
    const {
      selectedDate,
      selectedTime,
      selectedTimeZone,
      selectedDateError,
      isDateAuto,
    } = this.state
    const {deliveryMaxSelectableDate, onDismiss} = this.props
    const dateOptions = deliveryMaxSelectableDate
      ? this.dateOptions.filter(
          (dateOption) =>
            !isAfter(dateOption, deliveryMaxSelectableDate),
        )
      : this.dateOptions
    let timeOptions = buildTimeOptions(this.state.selectedDate).filter(
      isFuture
    )

    //check if the selected time is in array, otherwise add the time at the first position of array
    if (selectedTime) {
      const indexOfTimeSelected = timeOptions.findIndex((x) => {
        return (
          x.getHours() === selectedTime.getHours() &&
          x.getMinutes() === selectedTime.getMinutes()
        )
      })
      if (indexOfTimeSelected < 0) {
        timeOptions.unshift(selectedTime)
      }
    }

    return (
      <div className={'modal'} onClick={onDismiss}>
        <div className={'modal__body'} onClick={this.preventDismiss}>
          <div className={'modal__header'}>
            <h6>{i18n.t('Components.DeliveryDateModal.title')}</h6>
            <div className={'modal__header-close'}>
              <div className={'iconClose'} onClick={onDismiss}>
                <Close />
              </div>
            </div>
          </div>
          <div className={'modal__content'}>
            <div className={'delivery-date'}>
              <div
                className={`delivery-date__option ${
                  isDateAuto ? 'delivery-date__option--selected' : ''
                }`}
                onClick={this.setIsDateAuto}
              >
                <div className={'delivery-date__option-head'}>
                  <span
                    className={`radio ${isDateAuto ? 'radio--selected' : ''}`}
                    role="checkbox"
                    aria-checked={isDateAuto}
                    aria-labelledby="delivery-date-auto-label"
                  >
                    {' '}
                  </span>
                  <label
                    id="delivery-date-auto-label"
                    className={'delivery-date__option-name'}
                  >
                    {i18n.t('Components.DeliveryDateModal.auto')}
                  </label>
                </div>
              </div>
              <div
                className={`delivery-date__option ${
                  isDateAuto ? '' : 'delivery-date__option--selected'
                }`}
                onClick={this.setIsNotDateAuto}
              >
                <div className={'delivery-date__option-head'}>
                  <span
                    className={`radio ${isDateAuto ? '' : 'radio--selected'}`}
                    role="checkbox"
                    aria-checked={!isDateAuto}
                    aria-labelledby="delivery-data-guaranteed-by-label"
                  >
                    {' '}
                  </span>
                  <label
                    id="delivery-data-guaranteed-by-label"
                    className={'delivery-date__option-name'}
                  >
                    {i18n.t(
                      'Components.DeliveryDateModal.deliveryGuaranteedBy',
                    )}
                  </label>
                </div>
                <div className={'delivery-date__option-body'}>
                  <div className={'select-with-icon__wrapper'}>
                    <select
                      disabled={isDateAuto}
                      className={'select--compact delivery-date__select-day'}
                      value={selectedDate}
                      onChange={this.onSelectedDateSelectChange}
                    >
                      {dateOptions.map((date, key) => (
                        <option key={key} value={date}>
                          {dateFnsLocalizedFormat(date, 'eee d MMMM')}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={'select-with-icon__right-icon'} />
                  </div>
                  <div className={'select-with-icon__wrapper'}>
                    <select
                      disabled={isDateAuto}
                      className={'select--compact'}
                      value={selectedTime}
                      onChange={this.onSelectedTimeSelectChange}
                    >
                      {timeOptions.map((date, key) => (
                        <option key={key} value={date}>
                          {dateFnsLocalizedTimeFormat(date)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={'select-with-icon__right-icon'} />
                  </div>
                  <div
                    className={
                      'select-with-icon__wrapper delivery-date__time-zone-select-wrapper'
                    }
                  >
                    <select
                      disabled={isDateAuto}
                      className={'select--compact'}
                      value={selectedTimeZone.id}
                      onChange={this.onSelectedTimeZoneSelectChange}
                    >
                      {timeZones.map((timeZone, key) => (
                        <option key={key} value={timeZone.id}>
                          {timeZone.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={'select-with-icon__right-icon'} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={'modal__footer delivery-date__modal__footer'}>
            {selectedDateError && (
              <p className={'delivery-date__modal__footer--failure'}>
                {i18n.t('Components.DeliveryDateModal.invalidTime')}
              </p>
            )}
            <button
              type={'button'}
              className={'button--compact'}
              onClick={this.onAddDeliveryDate}
              disabled={selectedDateError}
            >
              {i18n.t('Components.DeliveryDateModal.submitButton')}
            </button>
          </div>
        </div>
      </div>
    )
  }
}
